// Checks with pdf.js, which has its own PDF interpreter, so they catch what
// our engine verified but misread. After every edit: pdf.js' reading of the
// page before and after, outside the edit regions. After every export: the
// written file against the shown document.
import { compareOutsideRegions, quadBounds, type Replacement, type TextItemLike } from '../engine/check/text-items.ts';
import { openForViewing, type PdfjsDocument } from './pdfjs.ts';
import type { FieldWidget, SearchLayer } from '../engine/worker/protocol.ts';

async function pageItems(bytes: Uint8Array, pageIndex: number, password?: string): Promise<TextItemLike[]> {
  const handle = openForViewing(bytes, password);
  try {
    const doc = await handle.promise;
    const page = await doc.getPage(pageIndex + 1);
    const content = await page.getTextContent();
    const items: TextItemLike[] = [];
    for (const it of content.items) if ('str' in it) items.push({ str: it.str, transform: it.transform, width: it.width, height: it.height, dir: it.dir });
    return items;
  } finally {
    await handle.destroy();
  }
}

/** `password` opens `before` when it is still the encrypted original (edited versions are not encrypted in memory). */
/**
 * `replace`: the span's text before and after an edit (or a delete); the items
 * that are part of the edit must read as the original with that replacement.
 * A move has none (its text reads in another order).
 */
export async function independentEditCheck(before: Uint8Array, after: Uint8Array, pageIndex: number, regions: [number, number][][][], password?: string, replace?: Replacement | Replacement[]): Promise<string[]> {
  const boxes = regions.map((g) => quadBounds(g, 1));
  const [a, b] = await Promise.all([pageItems(before, pageIndex, password), pageItems(after, pageIndex, password)]);
  return compareOutsideRegions(a, b, boxes, 0.001, replace);
}

const pageText = async (doc: PdfjsDocument, i: number): Promise<TextItemLike[]> =>
  (await (await doc.getPage(i + 1)).getTextContent()).items.flatMap((it) => ('str' in it ? [{ str: it.str, transform: it.transform, width: it.width, height: it.height, dir: it.dir }] : []));

/** Document information pdf.js reports, as text (the keys an export must keep). */
async function infoText(doc: PdfjsDocument): Promise<string> {
  const { info } = (await doc.getMetadata()) as { info: Record<string, unknown> };
  const keys = ['Title', 'Author', 'Subject', 'Keywords', 'Creator', 'Producer', 'CreationDate', 'ModDate', 'Trapped', 'Custom'];
  return JSON.stringify(keys.map((k) => [k, info[k] ?? null]));
}

/**
 * After an export: pdf.js (not our engine) opens the written file, which must
 * have the shown document's pages, text and document information. The
 * engine's own verifier reads both sides with the same parser, so a misreading
 * of the input would pass it (M1 review).
 */
export async function independentExportCheck(shown: PdfjsDocument, exported: Uint8Array, password?: string, layers: SearchLayer[] = []): Promise<string[]> {
  const handle = openForViewing(exported, password);
  try {
    const out = await handle.promise;
    if (out.numPages !== shown.numPages) return [`${shown.numPages} pages shown, ${out.numPages} exported`];
    const problems: string[] = [];
    if ((await infoText(out)) !== (await infoText(shown))) problems.push('the document information (title, author, …) differs');
    for (let i = 0; i < shown.numPages && problems.length < 3; i++) {
      // A page that got searchable text (M6 step 7): just that is new there, where its words are, reading as they do.
      // The scanner's text it replaces is gone; the new text is drawn last, so it reads after whatever else the page
      // draws there (an edit's real text, Bug2): found in one piece anywhere in the edited part, not in the old place.
      const layer = layers.find((l) => l.pageIndex === i);
      const diff = layer
        ? compareOutsideRegions(await pageText(shown, i), await pageText(out, i), layer.regions.map((g) => quadBounds(g, 1)), 0.001, [
            { from: layer.from, to: '' },
            { from: '', to: layer.to },
          ])
        : compareOutsideRegions(await pageText(shown, i), await pageText(out, i), []);
      if (diff.length) problems.push(`page ${i + 1}: ${diff[0]}`);
    }
    return problems;
  } catch (e) {
    return [`the exported file could not be opened (${e instanceof Error ? e.message : String(e)})`];
  } finally {
    await handle.destroy();
  }
}

/**
 * After filling in a field (M8, AC-ACRO-4): pdf.js' own reading of the field's widgets in the written file agrees
 * with what the engine says it wrote (text, combo, list and checkbox values; radio groups are checked by the engine).
 */
export async function independentFieldCheck(after: Uint8Array, password: string | undefined, widgets: FieldWidget[]): Promise<string[]> {
  const handle = openForViewing(after, password);
  try {
    const doc = await handle.promise;
    const all = ((await doc.getFieldObjects()) ?? {}) as unknown as Map<string, { id: string; value: unknown }[]> | Record<string, { id: string; value: unknown }[]>;
    const list = (all instanceof Map ? [...all.values()] : Object.values(all)).flat();
    const problems: string[] = [];
    for (const w of widgets) {
      if (w.kind === 'radio') continue;
      const f = list.find((x) => x.id === `${w.id.split(' ')[0]}R`);
      if (!f) {
        problems.push(`pdf.js does not find the field ${w.name}`);
        continue;
      }
      // (pdf.js reports a list box's first chosen option only.)
      const want = w.kind === 'checkbox' ? (w.value === w.onState ? w.onState : 'Off') : w.kind === 'list' ? (Array.isArray(w.value) ? (w.value[0] ?? '') : w.value) : (w.value ?? '');
      if (JSON.stringify(f.value ?? '') !== JSON.stringify(want ?? '')) problems.push(`pdf.js reads the field ${w.name} differently`);
    }
    return problems;
  } finally {
    await handle.destroy();
  }
}
